import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { acquireAudioQueueLock, deleteRecording, getCaseIdFromKey, loadAllPendingRecordings, releaseAudioQueueLock } from '../Utils/audioQueue';

const WebSocketContext = createContext();

export const useWebSocket = () => useContext(WebSocketContext);

// Dedicated hook for accessing user information throughout the app
// This provides a cleaner API and includes computed properties
export const useUser = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useUser must be used within a WebSocketProvider');
  }
  
  return useMemo(() => ({
    username: context.userInfo?.username || '',
    permissions: context.userInfo?.permissions || '',
    isReadOnly: context.userInfo?.permissions === 'view_only',
    isAdmin: context.userInfo?.permissions === 'admin',
    isEdit: context.userInfo?.permissions === 'edit',
    isLoggedIn: context.isUserLoggedIn,
    mfaSetupRequired: context.mfaSetupRequired,
    // Include the full userInfo for backward compatibility
    userInfo: context.userInfo
  }), [context.userInfo, context.isUserLoggedIn, context.mfaSetupRequired]);
};

// Commands that should NOT have session tokens added automatically
const SYSTEM_COMMANDS = [
  'login', 'check_login_status', 'validate_session', 'logout'
];

export const WebSocketProvider = ({ children }) => {
  const ws = useRef(null);
  // Default WebSocket URL - use same host/ip as website and match protocol
  const DEFAULT_WS_URL = (typeof window !== 'undefined' && window.location)
    ? `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.hostname}:6789`
    : 'ws://localhost:6789';
  const [isConnected, setIsConnected] = useState(false);
  const [isLoginRequired, setIsLoginRequired] = useState(null); // null = unknown, true/false = determined by server
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('dataunfold_logged_in');
    return stored === 'true';
  });
  const [isValidatingSession, setIsValidatingSession] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [userInfo, setUserInfo] = useState(() => ({
    username: '',
    permissions: '',
    get isReadOnly() { return this.permissions === 'view_only'; },
    get isAdmin() { return this.permissions === 'admin'; },
    get isEdit() { return this.permissions === 'edit'; }
  }));
  const [mfaSetupRequired, setMfaSetupRequired] = useState(() => {
    return localStorage.getItem('dataunfold_mfa_setup_required') === 'true';
  });
  const [lastCaseCreationUpdate, setLastCaseCreationUpdate] = useState(() => {
    return localStorage.getItem('dataunfold_last_case_creation_update') || '';
  });
  const [lastCaseCreationUpdateAt, setLastCaseCreationUpdateAt] = useState(() => {
    const stored = localStorage.getItem('dataunfold_last_case_creation_update_at');
    const parsed = stored ? Number(stored) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });
  const messageSubscribers = useRef([]);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 9999999999; // effectively unlimited
  const reconnectInterval = 3000; // 3 seconds
  const suppressReconnectRef = useRef(false);
  const authTimeoutRef = useRef(null); // Timeout for authentication
  const lastMessageTimeRef = useRef(Date.now());
  const sessionToken = useRef(localStorage.getItem('dataunfold_session_token'));
  const messageQueue = useRef([]); // Queue for messages while session is being validated
  const audioUploadMapRef = useRef(new Map());
  const [storageInfo, setStorageInfo] = useState({
    restrictToUserFolder: false,
    path: ''
  });
  const storageStatusRequestedRef = useRef(false);
  const [serverInfo, setServerInfo] = useState(null);
  // Server URL state + ref (allows switching servers at runtime)
  const [serverUrl, setServerUrlState] = useState(() => {
    return localStorage.getItem('dataunfold_ws_url') || DEFAULT_WS_URL;
  });
  const serverUrlRef = useRef(serverUrl);

  // Normalize and validate a server URL or host input
  const normalizeServerUrl = (input) => {
    if (!input) return DEFAULT_WS_URL;
    let trimmed = String(input).trim();
    if (!trimmed) return DEFAULT_WS_URL;

    // Already a websocket URL
    if (/^wss?:\/\//i.test(trimmed)) return trimmed;

    // Convert http(s) -> ws(s)
    if (/^https?:\/\//i.test(trimmed)) {
      return trimmed.replace(/^http/i, 'ws');
    }

    // host:port -> ws://host:port
    if (/^[\w.-]+:\d+$/.test(trimmed)) {
      return `ws://${trimmed}`;
    }

    // bare hostname/IP -> ws://host:6789
    if (/^[\w.-]+$/.test(trimmed)) {
      return `ws://${trimmed}:6789`;
    }

    // fallback
    return DEFAULT_WS_URL;
  };

  const AUDIO_UPLOAD_TIMEOUT_MS = 30000;

  const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    } catch (error) {
      reject(error);
    }
  });

  // Setter for server URL - normalizes, persists (optional), clears session and reconnects
  const setServerUrl = (newUrl, persist = true) => {
    const normalized = normalizeServerUrl(newUrl);
    serverUrlRef.current = normalized;
    setServerUrlState(normalized);

    try {
      if (persist) {
        localStorage.setItem('dataunfold_ws_url', normalized);
      } else {
        localStorage.removeItem('dataunfold_ws_url');
      }
    } catch (e) {
      console.warn('Failed to persist server URL', e);
    }

    // When switching servers, clear any previous session info - tokens are server-specific
    setIsUserLoggedIn(false);
    localStorage.setItem('dataunfold_logged_in', 'false');
    localStorage.removeItem('dataunfold_session_token');
    sessionToken.current = null;
    setSessionExpired(false);
    setMfaSetupRequired(false);
    localStorage.removeItem('dataunfold_mfa_setup_required');
    messageQueue.current = [];
    setIsConnected(false);
    setIsValidatingSession(false);
    setIsLoginRequired(null);
    reconnectAttempts.current = 0;
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }

    // Close current socket and reconnect to the new server
    if (ws.current) {
      ws.current.onclose = null; // avoid triggering reconnect loop
      try { ws.current.close(); } catch (e) { /* ignore */ }
      ws.current = null;
    }

    // Re-establish connection using new URL
    connect();
  };

  const clearCustomServer = () => {
    setServerUrl(DEFAULT_WS_URL, false);
  };

  const connect = useCallback(() => {
    suppressReconnectRef.current = false;
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      console.log("WebSocket already connected or connecting.");
      return; // Avoid reconnecting if already connected or in progress
    }

  const urlToUse = serverUrlRef.current || DEFAULT_WS_URL;
  console.log(`Connecting to WebSocket server at ${urlToUse}`);
  const socket = new WebSocket(urlToUse);

    // Add a connection timeout to prevent hanging in CONNECTING state
    const connectionTimeout = setTimeout(() => {
      if (socket.readyState === WebSocket.CONNECTING) {
        console.error("WebSocket connection timeout - closing hung connection");
        try { socket.close(); } catch (e) {}
      }
    }, 15000); // 15s timeout for connection

    socket.onopen = () => {
      clearTimeout(connectionTimeout);
      console.log("Connected to the server");
      ws.current = socket;
      setIsConnected(true);
      reconnectAttempts.current = 0;
      lastMessageTimeRef.current = Date.now();
      storageStatusRequestedRef.current = false;
      
      // Request login status when connected
      setTimeout(() => {
        if (socket.readyState === WebSocket.OPEN) {
          const token = sessionToken.current || localStorage.getItem('dataunfold_session_token');
          
          // Check if we're on a public route - if so, skip authentication checks
          const isPublicRoute = window.location.pathname.startsWith('/public/case/');
          
          if (isPublicRoute) {
            console.log('Public route detected on connection - skipping authentication');
            setIsValidatingSession(false);
            setIsLoginRequired(false); // Don't require login for public routes
            setIsUserLoggedIn(false); // User is not logged in, but that's OK for public routes
            return; // Skip authentication entirely
          }
          
          // Set authentication timeout (30 seconds)
          authTimeoutRef.current = setTimeout(() => {
            console.error('Authentication timeout - stopping validation');
            setIsValidatingSession(false);
            if (socket.readyState !== WebSocket.OPEN) {
              setIsLoginRequired(false); // Assume no login required if server is unreachable
              setIsConnected(false);
            }
          }, 30000);
          
          if (token) {
            // Try to authenticate with stored session token
            setIsValidatingSession(true);
            socket.send(JSON.stringify({
              command: 'validate_session',
              data: { session_token: token }
            }));
          } else {
            // No token, check login status to determine if login is required
            setIsValidatingSession(true); // Set validation state for login status check too
            socket.send(JSON.stringify({
              command: 'check_login_status',
              data: {}
            }));
          }
        }
      
          // Request basic server info for UI gating (platform, capabilities)
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ command: 'get_server_info', data: {} }));
          }
        }, 100); // Small delay to ensure connection is stable
    };    
    socket.onmessage = (event) => {
      lastMessageTimeRef.current = Date.now();
      // Parse JSON message before passing to subscribers
      let parsedMessage;
      try {
        parsedMessage = JSON.parse(event.data);
      } catch (error) {
        console.error("Error parsing message:", error);
        return;
      }

      if (parsedMessage.command === 'storage_status') {
        const data = parsedMessage.data || {};
        setStorageInfo((prev) => ({
          restrictToUserFolder: Boolean(data.restrict_to_user_folder ?? prev.restrictToUserFolder),
          path: data.path || prev.path || ''
        }));
      }

      if (parsedMessage.command === 'server_info') {
        setServerInfo(parsedMessage.data || {});
      }

      if (parsedMessage.command === 'UpdateCreationCase') {
        const text = parsedMessage?.data?.message ?? parsedMessage?.data?.value ?? '';
        if (text) {
          const now = Date.now();
          setLastCaseCreationUpdate(text);
          setLastCaseCreationUpdateAt(now);
          try {
            localStorage.setItem('dataunfold_last_case_creation_update', text);
            localStorage.setItem('dataunfold_last_case_creation_update_at', String(now));
          } catch (e) {
            // Ignore storage failures (e.g., private mode)
          }
        }
      }

      // Handle login-related messages
      if (parsedMessage.command === 'login_response') {
        // Any login response means the server is reachable; clear auth timeout and validation state.
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        setIsValidatingSession(false);
        if (socket.readyState === WebSocket.OPEN) {
          setIsConnected(true);
        }

        if (parsedMessage.data && parsedMessage.data.success) {
          setIsUserLoggedIn(true);
          setSessionExpired(false);
          localStorage.setItem('dataunfold_logged_in', 'true');
          const setupRequired = !!parsedMessage.data?.mfa_setup_required;
          setMfaSetupRequired(setupRequired);
          if (setupRequired) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
          
          // Store session token if provided
          if (parsedMessage.data.session_token) {
            sessionToken.current = parsedMessage.data.session_token;
            localStorage.setItem('dataunfold_session_token', parsedMessage.data.session_token);
          }

          if (parsedMessage.data.username || parsedMessage.data.permissions) {
            const permissions = parsedMessage.data.permissions || '';
            setUserInfo({
              username: parsedMessage.data.username || '',
              permissions: permissions,
              get isReadOnly() { return permissions === 'view_only'; },
              get isAdmin() { return permissions === 'admin'; },
              get isEdit() { return permissions === 'edit'; }
            });
          }

          // Flush any queued messages now that login succeeded.
          const queuedMessages = messageQueue.current;
          messageQueue.current = [];
          if (queuedMessages.length) {
            console.log(`Processing ${queuedMessages.length} queued messages after login`);
            queuedMessages.forEach(message => {
              if (socket.readyState === WebSocket.OPEN) {
                const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
                if (!SYSTEM_COMMANDS.includes(message.command) && currentToken) {
                  message.data = message.data || {};
                  if (!message.data.session_token) {
                    message.data.session_token = currentToken;
                    console.log(`Re-adding session token to queued command ${message.command}`);
                  }
                }
                socket.send(JSON.stringify(message));
              }
            });
          }
          
          console.log('User logged in successfully');
        } else {
          setIsUserLoggedIn(false);
          localStorage.setItem('dataunfold_logged_in', 'false');
          localStorage.removeItem('dataunfold_session_token');
          sessionToken.current = null;
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
          console.log('Login failed:', parsedMessage.data?.message || 'Unknown error');
        }
      } else if (parsedMessage.command === 'check_login_status') {
        // Handle the check_login_status response from server
        const loginRequired = parsedMessage.data?.login_required || false;
        const isLogged = parsedMessage.data?.is_logged || false;
        
        // Clear authentication timeout
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        
        setIsLoginRequired(loginRequired);
        setIsValidatingSession(false); // Reset validation state
        
        if (isLogged) {
          setIsUserLoggedIn(true);
          setSessionExpired(false);
          localStorage.setItem('dataunfold_logged_in', 'true');
          
          // Store user info if available
          if (parsedMessage.data.username && parsedMessage.data.permissions) {
            const permissions = parsedMessage.data.permissions;
            setUserInfo({
              username: parsedMessage.data.username,
              permissions: permissions,
              get isReadOnly() { return permissions === 'view_only'; },
              get isAdmin() { return permissions === 'admin'; },
              get isEdit() { return permissions === 'edit'; }
            });
          }
        } else {
          setIsUserLoggedIn(false);
          localStorage.setItem('dataunfold_logged_in', 'false');
          
          // Clear session token if user is not logged in
          localStorage.removeItem('dataunfold_session_token');
          sessionToken.current = null;
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
        }
        
        console.log('Login status check - required:', loginRequired, 'logged_in:', isLogged);
        
        // If login is required and user is not logged in, redirect to login page
        // BUT: Don't redirect if on a public case route
        const isPublicRoute = window.location.pathname.startsWith('/public/case/');
        console.log('Current pathname:', window.location.pathname, 'isPublicRoute:', isPublicRoute);
        if (loginRequired && !isLogged && !isPublicRoute && window.location.pathname !== '/login') {
          console.log('Login required but user not logged in - redirecting to login page');
          window.location.href = '/login';
        } else if (isPublicRoute) {
          console.log('Public route detected, skipping login redirect');
        }
      } else if (parsedMessage.command === 'login_required') {
        // Keep the old handler for backward compatibility
        const required = parsedMessage.data?.required || false;
        const loggedIn = parsedMessage.data?.logged_in || false;
        setIsLoginRequired(required);
        
        if (loggedIn) {
          setIsUserLoggedIn(true);
          setSessionExpired(false);
          localStorage.setItem('dataunfold_logged_in', 'true');
        } else {
          setIsUserLoggedIn(false);
          localStorage.setItem('dataunfold_logged_in', 'false');
        }
        
        console.log('Login status - required:', required, 'logged_in:', loggedIn);
      } else if (parsedMessage.command === 'mfa_setup_required') {
        setMfaSetupRequired(true);
        localStorage.setItem('dataunfold_mfa_setup_required', 'true');
      } else if (parsedMessage.command === 'get_mfa_status') {
        if (parsedMessage.data?.success) {
          const required = !!parsedMessage.data.required;
          const enabled = !!parsedMessage.data.enabled;
          const setupRequired = required && !enabled;
          setMfaSetupRequired(setupRequired);
          if (setupRequired) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
        }
      } else if (parsedMessage.command === 'verify_mfa_setup') {
        if (parsedMessage.data?.success) {
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
        }
      } else if (parsedMessage.command === 'disable_mfa') {
        if (parsedMessage.data?.success) {
          const msg = (parsedMessage.data?.message || '').toLowerCase();
          if (msg.includes('setup required')) {
            setMfaSetupRequired(true);
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            setMfaSetupRequired(false);
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
        }
      } else if (parsedMessage.command === 'set_global_mfa_requirement') {
        if (parsedMessage.data?.success && parsedMessage.data.hasOwnProperty('mfa_setup_required')) {
          const setupRequired = !!parsedMessage.data.mfa_setup_required;
          setMfaSetupRequired(setupRequired);
          if (setupRequired) {
            localStorage.setItem('dataunfold_mfa_setup_required', 'true');
          } else {
            localStorage.removeItem('dataunfold_mfa_setup_required');
          }
        }
      } else if (parsedMessage.command === 'logout') {
        setIsUserLoggedIn(false);
        setSessionExpired(false);
        localStorage.setItem('dataunfold_logged_in', 'false');
        localStorage.removeItem('dataunfold_session_token');
        sessionToken.current = null;
        setMfaSetupRequired(false);
        localStorage.removeItem('dataunfold_mfa_setup_required');
        console.log('User logged out by server - redirecting to login page');
        // Redirect to login page only if not already there and not on public route
        const isPublicRouteLogout = window.location.pathname.startsWith('/public/case/');
        if (!isPublicRouteLogout && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (parsedMessage.command === 'session_invalid') {
        // Server says session is invalid, force logout
        setIsUserLoggedIn(false);
        setSessionExpired(true);
        localStorage.setItem('dataunfold_logged_in', 'false');
        localStorage.removeItem('dataunfold_session_token');
        sessionToken.current = null;
        setMfaSetupRequired(false);
        localStorage.removeItem('dataunfold_mfa_setup_required');
        setIsValidatingSession(false);
        console.log('Session invalidated by server - redirecting to login page');
        // Clear queued messages since session is invalid
        messageQueue.current = [];
        // Redirect to login page only if not already there and not on public route
        const isPublicRouteInvalid = window.location.pathname.startsWith('/public/case/');
        if (!isPublicRouteInvalid && window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      } else if (parsedMessage.command === 'session_validated' || parsedMessage.command === 'validate_session') {
        // Handle session validation response
        // Clear authentication timeout
        if (authTimeoutRef.current) {
          clearTimeout(authTimeoutRef.current);
          authTimeoutRef.current = null;
        }
        
        setIsValidatingSession(false);
        
        // Check for both 'valid' and 'success' fields for compatibility
        const isValid = (parsedMessage.data && (parsedMessage.data.valid === true || parsedMessage.data.success === true));
        
        if (isValid) {
          setIsUserLoggedIn(true);
          setSessionExpired(false);
          setIsLoginRequired(true); // If session validation was attempted, login is required
          localStorage.setItem('dataunfold_logged_in', 'true');
          
          // Store user info from session validation
          if (parsedMessage.data.username && parsedMessage.data.permissions) {
            const permissions = parsedMessage.data.permissions;
            setUserInfo({
              username: parsedMessage.data.username,
              permissions: permissions,
              get isReadOnly() { return permissions === 'view_only'; },
              get isAdmin() { return permissions === 'admin'; },
              get isEdit() { return permissions === 'edit'; }
            });
          }
          
          console.log('Session validated successfully');
          
          // Process queued messages
          const queuedMessages = messageQueue.current;
          messageQueue.current = [];
          console.log(`Processing ${queuedMessages.length} queued messages`);
          queuedMessages.forEach(message => {
            if (socket.readyState === WebSocket.OPEN) {
              // Ensure session token is present in queued messages (except system commands)
              const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
              if (!SYSTEM_COMMANDS.includes(message.command) && currentToken) {
                message.data = message.data || {};
                if (!message.data.session_token) {
                  message.data.session_token = currentToken;
                  console.log(`Re-adding session token to queued command ${message.command}`);
                }
              }
              socket.send(JSON.stringify(message));
            }
          });
        } else {
          setIsUserLoggedIn(false);
          setSessionExpired(false);
          setUserInfo({ 
            username: '', 
            permissions: '',
            get isReadOnly() { return false; },
            get isAdmin() { return false; },
            get isEdit() { return false; }
          });
          localStorage.setItem('dataunfold_logged_in', 'false');
          setMfaSetupRequired(false);
          localStorage.removeItem('dataunfold_mfa_setup_required');
          
          // Only redirect if there was a session token (user was previously logged in)
          const hadSessionToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
          
          localStorage.removeItem('dataunfold_session_token');
          sessionToken.current = null;
          console.log('Session validation failed:', parsedMessage.data?.message);
          // Clear queued messages since validation failed
          messageQueue.current = [];
          
          // Set login required to true for failed validation
          setIsLoginRequired(true);
          if (hadSessionToken) {
            const sessionMessage = (parsedMessage.data?.message || '').toLowerCase();
            const isExpired = sessionMessage.includes('expired') || sessionMessage.includes('invalid');
            if (isExpired) {
              setSessionExpired(true);
              const isPublicRouteExpired = window.location.pathname.startsWith('/public/case/');
              if (!isPublicRouteExpired && window.location.pathname !== '/login') {
                console.log('Session expired - redirecting to login page');
                window.location.href = '/login';
              }
            }
          }
          
          // If user had a session token but validation failed, check login requirements
          if (hadSessionToken) {
            console.log('Session invalid, checking login requirements...');
            // Check if login is required instead of immediately redirecting
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({
                command: 'check_login_status',
                data: {}
              }));
            }
          } else {
            // No session token was present, check login requirements
            if (ws.current && ws.current.readyState === WebSocket.OPEN) {
              ws.current.send(JSON.stringify({
                command: 'check_login_status',
                data: {}
              }));
            }
          }
        }
      }

      // Pass the parsed JSON directly to subscribers instead of the raw event
      messageSubscribers.current.forEach((callback) => callback(parsedMessage));
    };

    socket.onclose = () => {
      clearTimeout(connectionTimeout);
      console.log("Disconnected from the server");
    setIsConnected(false);
    setIsValidatingSession(false);
    setStorageInfo({ restrictToUserFolder: false, path: '' });
      
      // Clear authentication timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      
      if (suppressReconnectRef.current) {
        console.log("WebSocket closed intentionally - skipping reconnect.");
        suppressReconnectRef.current = false;
        reconnectAttempts.current = 0;
        return;
      }

      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current += 1;
        setTimeout(() => {
          connect();
        }, reconnectInterval);
      } else {
        console.error("Max reconnect attempts reached. Could not reconnect to the server.");
        // Fallback to offline mode - assume no login required
        setIsLoginRequired(false);
        setIsUserLoggedIn(false);
      }
    };

    socket.onerror = (error) => {
      clearTimeout(connectionTimeout);
      console.error("WebSocket error:", error);
      setIsConnected(false);
      setIsValidatingSession(false);
      
      // Clear authentication timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      
      // If this is the initial connection attempt and it fails, fallback to offline mode
      // Only do this if we don't have a saved session, to avoid logging out active users on transient errors
      const hasToken = localStorage.getItem('dataunfold_session_token');
      if (reconnectAttempts.current === 0 && !hasToken) {
        console.log('Initial connection failed, falling back to offline mode');
        setIsLoginRequired(false);
        setIsUserLoggedIn(false);
      }
    };
  }, []); // useCallback dependency array

  useEffect(() => {
    // Only auto-connect on mount if we already have a session token
    // or if we're on a public route that needs to fetch public data.
    const hasToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
    const isPublicRoute = window.location.pathname.startsWith('/public/case/');

    if (hasToken || isPublicRoute) {
      connect(); // Establish WebSocket connection
    } else {
      // No token & not public - avoid connecting by default. Require login.
      setIsValidatingSession(false);
      setIsConnected(false);
      setIsUserLoggedIn(false);
      setIsLoginRequired(true);
    }

    return () => {
      // Clear authentication timeout
      if (authTimeoutRef.current) {
        clearTimeout(authTimeoutRef.current);
        authTimeoutRef.current = null;
      }
      
      if (ws.current) {
        ws.current.onclose = null; // Prevent triggering reconnection during cleanup
        ws.current.close(); // Close the WebSocket connection when the component unmounts
      }
      messageSubscribers.current = []; // Clear message subscribers
      messageQueue.current = []; // Clear message queue
    };
  }, [connect]); // connect is now stable due to useCallback

  // Mobile/Idle recovery: Reconnect when app becomes visible or focused
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const timeSinceLastMessage = now - lastMessageTimeRef.current;
        const hasToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
        const isPublicRoute = window.location.pathname.startsWith('/public/case/');
        
        // If not connected OR if same connection has been silent for too long (e.g. 60s)
        // 60s is reasonable because server pings every 20s. If we missed 3 pings, it's definitely dead.
        if (!isConnected || (ws.current && timeSinceLastMessage > 60000)) {
          if (hasToken || isPublicRoute) {
            console.log(`App resumed (timeSinceLastMessage: ${timeSinceLastMessage}ms). Reconnecting...`);
            
            if (ws.current) {
              try {
                // Remove onclose to prevent triggering the built-in delay if we want to reconnect immediately
                ws.current.onclose = null;
                ws.current.close();
              } catch (e) {}
              ws.current = null;
            }
            
            setIsConnected(false);
            reconnectAttempts.current = 0; // Reset attempts to try immediately
            connect();
          }
        } else {
          console.log('App resumed, connection still healthy.');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, [connect, isConnected]);

  useEffect(() => {
    if (isConnected && isUserLoggedIn && ws.current && ws.current.readyState === WebSocket.OPEN && !storageStatusRequestedRef.current) {
      try {
        ws.current.send(JSON.stringify({ command: 'get_storage_status' }));
        storageStatusRequestedRef.current = true;
      } catch (e) {
        console.warn('Failed to request storage status', e);
      }
    }
  }, [isConnected, isUserLoggedIn]);

  const subscribeToMessages = (commands, callback) => {
    // Check if we're getting old-style single callback or new-style filtered callback
    if (typeof commands === 'function') {
      // Old style: single callback for all messages
      const actualCallback = commands;
      messageSubscribers.current.push(actualCallback);
      return () => {
        messageSubscribers.current = messageSubscribers.current.filter((cb) => cb !== actualCallback);
      };    } else if (Array.isArray(commands) && typeof callback === 'function') {
      // New style: filtered callback for specific commands
      const wrappedCallback = (message) => {
        if (message && message.command && commands.includes(message.command)) {
          // Validate that message.data is an object before passing to callback
          if (message.data && typeof message.data === 'object') {
            callback(message);
          } else if (message.data === undefined || message.data === null) {
            // Create a standardized data object if missing
            message.data = {};
            callback(message);
          } else {
            // Handle non-object data by wrapping it in an object
            const wrappedMessage = {
              ...message,
              data: { value: message.data }
            };
            callback(wrappedMessage);
          }
        }
      };
      
      messageSubscribers.current.push(wrappedCallback);
      return () => {
        messageSubscribers.current = messageSubscribers.current.filter((cb) => cb !== wrappedCallback);
      };
    } else {
      console.error('subscribeToMessages called with invalid parameters');
      return () => {}; // Return empty function as a fallback
    }
  };

  // Enhanced sendMessage function that handles session validation queuing
  const sendMessage = useCallback((messageToSend) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      console.error('WebSocket is not connected');
      return;
    }

    // Handle different message formats
    if (typeof messageToSend === 'string') {
      try {
        messageToSend = JSON.parse(messageToSend);
      } catch (e) {
        console.error('Failed to parse message:', e);
        return;
      }
    }

    // Always ensure we have the latest session token
    const currentToken = sessionToken.current || localStorage.getItem('dataunfold_session_token');
    
    // Add session token to all commands except system commands
    if (!SYSTEM_COMMANDS.includes(messageToSend.command)) {
      if (isValidatingSession && currentToken) {
        // Ensure message has session token before queuing
        messageToSend.data = messageToSend.data || {};
        if (!messageToSend.data.session_token) {
          messageToSend.data.session_token = currentToken;
        }
        
        // Queue the message until session validation completes
        messageQueue.current.push(messageToSend);
        console.log(`Queuing command ${messageToSend.command} until session validation completes`);
        return;
      }
      
      // Always add session token to commands if available
      if (currentToken && (!messageToSend.data || !messageToSend.data.session_token)) {
        messageToSend.data = messageToSend.data || {};
        messageToSend.data.session_token = currentToken;
        console.log(`Adding session token to command ${messageToSend.command}`);
      } else if (!currentToken) {
        // Only warn about missing token if not on a public route
        const isPublicRoute = window.location.pathname.startsWith('/public/case/');
        if (!isPublicRoute) {
          console.warn(`No session token available for command: ${messageToSend.command}`);
        }
      }
    }
    
    ws.current.send(JSON.stringify(messageToSend));
  }, [isValidatingSession]);

  const drainAudioUploadQueue = useCallback(async () => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    if (!isConnected || isValidatingSession) return;
    if (isLoginRequired === true && !isUserLoggedIn) return;

    if (!acquireAudioQueueLock()) return;

    try {
      const pendingRecords = await loadAllPendingRecordings();
      for (const record of pendingRecords) {
        if (!ws.current || ws.current.readyState !== WebSocket.OPEN) break;
        if (!record?.blob) continue;

        const caseIdValue = getCaseIdFromKey(record.caseId);
        if (!caseIdValue) continue;

        const filename = record.name || `recording-${new Date(record.createdAt || Date.now()).toISOString().replace(/[:.]/g, '-')}.webm`;
        const mimeType = record.mimeType || record.blob.type || 'audio/webm';
        let filedata;

        try {
          filedata = await blobToDataUrl(record.blob);
        } catch (error) {
          console.warn('[WebSocketContext] Failed to read queued audio blob', error);
          continue;
        }

        const ackPromise = new Promise((resolve) => {
          audioUploadMapRef.current.set(filename, { key: record.key, resolve });
        });

        sendMessage({
          command: 'uploadfile',
          caseid: caseIdValue,
          files: [{ filename, filedata }]
        });

        const result = await new Promise((resolve) => {
          let settled = false;
          const timeoutId = setTimeout(() => {
            if (settled) return;
            settled = true;
            audioUploadMapRef.current.delete(filename);
            resolve({ status: 'timeout' });
          }, AUDIO_UPLOAD_TIMEOUT_MS);

          ackPromise.then((payload) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(payload);
          });
        });

        if (result?.status === 'error' || result?.status === 'timeout') {
          break;
        }
      }
    } catch (error) {
      console.warn('[WebSocketContext] Failed to drain audio upload queue', error);
    } finally {
      releaseAudioQueueLock();
    }
  }, [isConnected, isLoginRequired, isUserLoggedIn, isValidatingSession, sendMessage]);

  // Logout function
  const logout = useCallback(() => {
    suppressReconnectRef.current = true;
    reconnectAttempts.current = 0;
    setIsConnected(false);
    setIsValidatingSession(false);
    setIsLoginRequired(true);
    setStorageInfo({ restrictToUserFolder: false, path: '' });
    storageStatusRequestedRef.current = false;

    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }

    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      // Send logout command to server using sendMessage to ensure proper token handling
      sendMessage({ command: 'logout' });
    }
    
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      try {
        ws.current.close(1000, 'User logout');
      } catch (e) {
        console.warn('Failed to close websocket on logout', e);
      }
    }
    ws.current = null;
    
    // Clear local state
    setIsUserLoggedIn(false);
    setUserInfo({ 
      username: '', 
      permissions: '',
      get isReadOnly() { return false; },
      get isAdmin() { return false; },
      get isEdit() { return false; }
    });
    setSessionExpired(false);
    setMfaSetupRequired(false);
    localStorage.setItem('dataunfold_logged_in', 'false');
    localStorage.removeItem('dataunfold_session_token');
    localStorage.removeItem('dataunfold_mfa_setup_required');
    localStorage.removeItem('dataunfold_last_case_creation_update');
    localStorage.removeItem('dataunfold_last_case_creation_update_at');
    sessionToken.current = null;
    setLastCaseCreationUpdate('');
    setLastCaseCreationUpdateAt(null);
    
    // Clear message queue
    messageQueue.current = [];
    
    console.log('User logged out - redirecting to login page');
    
    // Redirect to login page only if not already there
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }, [sendMessage]);

  useEffect(() => {
    if (!isConnected || isValidatingSession) return;
    if (isLoginRequired === true && !isUserLoggedIn) return;
    drainAudioUploadQueue();
  }, [drainAudioUploadQueue, isConnected, isLoginRequired, isUserLoggedIn, isValidatingSession]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages(['uploadfile'], (message) => {
      const { status, files, failed } = message.data || {};
      if (!Array.isArray(files) || files.length === 0) return;

      const failedSet = new Set(Array.isArray(failed) ? failed : []);
      files.forEach((filename) => {
        const entry = audioUploadMapRef.current.get(filename);
        if (!entry) return;

        audioUploadMapRef.current.delete(filename);
        const success = status !== 'error' && !failedSet.has(filename);
        if (success && entry.key) {
          deleteRecording(entry.key).catch((error) => {
            console.warn('[WebSocketContext] Failed to remove queued recording', error);
          });
        }

        if (typeof entry.resolve === 'function') {
          entry.resolve({ status, success });
        }
      });

      if (audioUploadMapRef.current.size === 0) {
        drainAudioUploadQueue();
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [drainAudioUploadQueue, subscribeToMessages]);

  return (
    <WebSocketContext.Provider value={{ 
      ws: ws.current, 
      isConnected, 
      subscribeToMessages,
      isLoginRequired,
      isUserLoggedIn,
      isValidatingSession,
      sessionExpired,
      userInfo,
      serverInfo,
      mfaSetupRequired,
      lastCaseCreationUpdate,
      lastCaseCreationUpdateAt,
      serverUrl: serverUrlRef.current,
      setServerUrl, // Allows switching to a custom server
      clearCustomServer,
      connect,
      setIsUserLoggedIn, // Allow manual control if needed
      sendMessage, // New method for sending messages with auto session token
      storageInfo,
      logout // Logout function
    }}>
      {children}
    </WebSocketContext.Provider>
  );
};
